import { useState } from 'react';
import type { FieldProps } from '@rjsf/utils';
import { Box, Button, CircularProgress, Menu, MenuItem, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import type { ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { Asset } from '../../Pages/Section/hooks/useGetAssets';
import { useUploadAsset } from '../../Pages/Section/hooks/useUploadAsset';
import { AssetPickerModal } from '../AssetPickerModal';
import { FileUploadModal } from '../FileUploadModal';
import { ImageCropModal } from '../ImageCropModal';
import { SelectedImageCard } from '../SelectedImageCard';

interface ImageItem {
  assetId: string;
  alt?: string;
  thumbnailUrl: string;
  imageUrl: string;
}

interface ImageFieldOptions {
  sources?: string[];
  addButtonLabel?: string;
  aspectRatio?: string;
}

function buildDefaultFileName(originalName: string, aspectRatio?: string): string {
  const lastDot = originalName.lastIndexOf('.');
  const base = lastDot >= 0 ? originalName.slice(0, lastDot) : originalName;
  const ext = lastDot >= 0 ? originalName.slice(lastDot) : '';
  const suffix = aspectRatio ? `-${aspectRatio.replace(':', 'x')}` : '';
  return `${base}${suffix}${ext}`;
}

const SOURCE_LABELS: Record<string, string> = {
  disk: 'From disk',
  assets: 'From assets',
};

export function ImageField(props: FieldProps<ImageItem>): ReactElement {
  const { formData, onChange, uiSchema, fieldPathId, rawErrors = [], errorSchema = {} } = props;
  // RJSF's default form state populates object fields as `{}`, so an empty or
  // incomplete object must count as "no image" — assetId is always set by both
  // selection paths, so its presence discriminates a real image.
  const image = formData?.assetId ? formData : null;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [cropFileName, setCropFileName] = useState<string>('');
  const [cropKey, setCropKey] = useState(0);
  const [searchParams] = useSearchParams();
  const organisationId = searchParams.get('organisationId') ?? '';
  const { mutate: uploadAsset, isPending } = useUploadAsset();

  const {
    sources = [],
    addButtonLabel = 'Add image',
    aspectRatio,
  } = (uiSchema?.['ui:options'] ?? {}) as ImageFieldOptions;

  // Required-property errors for a missing image attach to the nested property
  // paths (errorSchema.assetId etc.), not to the field itself, because this
  // field replaces the per-property SchemaFields that would normally show them.
  const nestedErrors = Object.values(errorSchema).flatMap(
    (propError) => (propError as { __errors?: string[] } | undefined)?.__errors ?? []
  );
  const hasError = !image && (rawErrors.length > 0 || nestedErrors.length > 0);
  const buttonColorClasses = hasError
    ? 'vtwo:text-red-600 vtwo:border-red-500'
    : 'vtwo:text-gray-600 vtwo:border-plus-border';

  function handleSourceClick(source: string) {
    setAnchorEl(null);
    if (source === 'disk') {
      setUploadModalOpen(true);
    } else if (source === 'assets') {
      setAssetPickerOpen(true);
    }
  }

  function handleFileSelected(file: File) {
    setUploadModalOpen(false);
    setCropFileName(buildDefaultFileName(file.name, aspectRatio));
    setCropKey((k) => k + 1);
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result as string);
    };
    reader.readAsDataURL(file);
  }

  function handleCropDone(file: File) {
    setCropImageSrc(null);
    uploadAsset(
      { file, organisationId },
      {
        onSuccess: ({ id, thumbnailUrl, url }) => {
          onChange({ assetId: id, thumbnailUrl, alt: '', imageUrl: url }, fieldPathId.path);
        },
      }
    );
  }

  function handleAssetSelect(asset: Asset) {
    onChange(
      { assetId: asset.id, thumbnailUrl: asset.thumbnailUrl, alt: '', imageUrl: asset.url },
      fieldPathId.path
    );
    setAssetPickerOpen(false);
  }

  function handleDelete() {
    onChange(undefined, fieldPathId.path);
  }

  return (
    <Box className="vtwo:flex vtwo:flex-col vtwo:gap-2">
      {image && (
        <SelectedImageCard
          thumbnailUrl={image.thumbnailUrl}
          alt={image.alt}
          isPending={isPending}
          onReplaceClick={(e) => setAnchorEl(e.currentTarget)}
          onDelete={handleDelete}
        />
      )}

      {!image && (
        <Button
          variant="outlined"
          startIcon={isPending ? <CircularProgress size={16} /> : <AddIcon />}
          fullWidth
          disabled={isPending}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          className={`vtwo:normal-case ${buttonColorClasses} vtwo:py-2 vtwo:rounded-lg`}
        >
          {addButtonLabel}
        </Button>
      )}

      {hasError && (
        <Typography variant="caption" className="vtwo:text-red-600">
          Image is required
        </Typography>
      )}

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {sources.map((source) => (
          <MenuItem key={source} onClick={() => handleSourceClick(source)}>
            {SOURCE_LABELS[source] ?? source}
          </MenuItem>
        ))}
      </Menu>

      <FileUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onFileSelected={handleFileSelected}
      />

      <ImageCropModal
        key={cropKey}
        open={Boolean(cropImageSrc)}
        imageSrc={cropImageSrc ?? ''}
        aspectRatio={aspectRatio}
        defaultFileName={cropFileName}
        onClose={() => setCropImageSrc(null)}
        onCropDone={handleCropDone}
      />

      <AssetPickerModal
        open={assetPickerOpen}
        onClose={() => setAssetPickerOpen(false)}
        onSelect={handleAssetSelect}
        organisationId={organisationId}
        aspectRatio={aspectRatio}
      />
    </Box>
  );
}
